import { svgPathToPathCommands } from '@/lib/elements/shape-path-commands'
import type { PenPathShapeLike } from './types/pen-path-shape-like'
import { parseSvgPathToAnchors } from './helpers/parse-svg-path-to-anchors'
import { anchorsToSvgPath } from './helpers/anchors-to-svg-path'
import { pathTightBounds } from './helpers/pen-path-bounds'

/**
 * Recompute a pen path shape's x/y/width/height so every anchor node lies inside
 * the bounding box (handles may extend outside), re-normalizing path coordinates
 * to the new top-left.
 */
export function refitPenPathShapeBounds<T extends PenPathShapeLike>(shape: T): T {
  if (!shape.path.trim()) return shape

  const { points, isClosed, subpaths } = parseSvgPathToAnchors(shape.path)

  if (points.length === 0) return shape

  // Tight bounds including bezier curve extrema — handles may extend outside, curve must not.
  const local = pathTightBounds(points, isClosed, subpaths)

  if (!local || !Number.isFinite(local.minX)) return shape

  const minX = shape.x + local.minX
  const minY = shape.y + local.minY
  const maxX = shape.x + local.maxX
  const maxY = shape.y + local.maxY

  const width = Math.max(1, maxX - minX)
  const height = Math.max(1, maxY - minY)

  const shiftX = shape.x - minX
  const shiftY = shape.y - minY

  const refittedPoints = points.map((pt) => ({
    ...pt,
    x: pt.x + shiftX,
    y: pt.y + shiftY,
    handleIn: pt.handleIn ? { x: pt.handleIn.x + shiftX, y: pt.handleIn.y + shiftY } : null,
    handleOut: pt.handleOut ? { x: pt.handleOut.x + shiftX, y: pt.handleOut.y + shiftY } : null,
  }))

  const path = anchorsToSvgPath(refittedPoints, isClosed, subpaths)
  const pathCommands = svgPathToPathCommands(path)

  return {
    ...shape,
    x: minX,
    y: minY,
    width,
    height,
    path,
    pathCommands,
  }
}
